// "Ask about my mail", pure parts: the page's URL search, the calm line for each outcome, where a
// citation links (MeeMail threads only), and what a checked quote says about itself.
import type { AskCitation, AskView } from "@shared/ask";

export interface AskSearch {
  /** A question to ask on arrival (from the palette). */
  q?: string | undefined;
  /** A stored answer to show. */
  id?: string | undefined;
}

export function parseAskSearch(raw: Record<string, unknown>): AskSearch {
  const out: AskSearch = {};
  if (typeof raw.q === "string" && raw.q.trim()) out.q = raw.q.trim().slice(0, 500);
  if (typeof raw.id === "string" && /^[\w-]{1,200}$/.test(raw.id)) out.id = raw.id;
  return out;
}

/** The line shown instead of (or above) an answer, or null when the answer speaks for itself. */
export function outcomeLine(view: Pick<AskView, "status" | "reason" | "parts">): string | null {
  switch (view.status) {
    case "answered":
      return null;
    case "not_found":
      return view.parts.length ? null : "Couldn't find this in your mail.";
    case "unchecked":
      return "Claude answered, but MeeMail couldn't match the answer to any email, so it isn't shown. Try asking another way.";
    case "unavailable":
      switch (view.reason) {
        case "no_model":
          return "Asking needs Claude, which isn't set up on this server yet.";
        case "budget":
          return "Today's budget for looking things up is used up. You can ask again tomorrow.";
        case "refused":
          return "Claude declined to look into this one.";
        default:
          return "Couldn't answer this just now. Try again, or ask it another way.";
      }
  }
}

/** Saving keeps the answer and its sources; only answers backed by a thread can be saved. */
export function canSave(view: Pick<AskView, "status" | "text" | "citations">): boolean {
  return (
    (view.status === "answered" || view.status === "not_found") &&
    !!view.text &&
    view.citations.length > 0
  );
}

/** The view after Undo took its saved answer out of the library: Save is offered again. */
export function withoutSavedAnswer<V extends Pick<AskView, "savedAnswerId">>(
  view: V | undefined,
  answerId: string,
): V | undefined {
  return view && view.savedAnswerId === answerId ? { ...view, savedAnswerId: null } : view;
}

/** Where a citation's quote was checked, in plain words. */
export function checkedLabel(c: Pick<AskCitation, "checked" | "in" | "filename">): string {
  if (!c.checked) return "The quote couldn't be checked against this email.";
  return c.in === "attachment" && c.filename
    ? `Checked against the attachment ${c.filename}.`
    : "Checked against the email.";
}

export function droppedLine(dropped: number): string | null {
  if (dropped <= 0) return null;
  return dropped === 1
    ? "Left out one source MeeMail couldn't match to your mail."
    : `Left out ${dropped} sources MeeMail couldn't match to your mail.`;
}

/** The thread key of a MeeMail thread link (`/t/<key>`), or null for anything else. */
export function mailThreadKey(url: string): string | null {
  const m = /^\/t\/([^/?#]+)$/.exec(url);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]!);
  } catch {
    return null;
  }
}
